import { useEffect, useMemo, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { RichContent } from '@/components/common/rich-content'
import { UserAvatar } from '@/components/common/badges'
import { useAddComment } from '@/lib/mutations'
import { useRepoMeta } from '@/lib/query'
import { useMe } from '@/hooks/use-me'
import { useMentionGuard } from '@/hooks/use-mention-guard'
import { cn } from '@/lib/utils'
import type { WorkIssue } from '@/types/github'

// Posted bodies are plain markdown: GitHub itself turns `@login` into a real
// mention (with notifications) server-side, so the autocomplete below only
// exists to get logins right. File uploads have no public API — that's what
// the "Comment on GitHub" link is for.

function useMentionState(value: string, caret: number) {
  // Depend on the stable query reference, not a fresh `?? []` each render
  // (which would churn the memo deps every render).
  const assignableUsers = useRepoMeta().data?.assignableUsers
  return useMemo(() => {
    const users = assignableUsers ?? []
    const upToCaret = value.slice(0, caret)
    const m = /(?:^|\s)@([\w-]*)$/.exec(upToCaret)
    if (!m) return null
    const fragment = m[1]!.toLowerCase()
    const matches = users
      .filter(
        (u) =>
          u.login.toLowerCase().startsWith(fragment) ||
          (u.name ?? '').toLowerCase().startsWith(fragment),
      )
      .slice(0, 6)
    if (matches.length === 0) return null
    return { fragment, matches, start: upToCaret.length - fragment.length }
  }, [value, caret, assignableUsers])
}

export function CommentComposer({ issue }: { issue: WorkIssue }) {
  const me = useMe()!
  const [body, setBody] = useState('')
  const [caret, setCaret] = useState(0)
  const [preview, setPreview] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [mentionDismissed, setMentionDismissed] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const optimisticSeq = useRef(0)
  const rawMention = useMentionState(body, caret)
  const mention = mentionDismissed ? null : rawMention
  const mentionGuardRef = useMentionGuard()
  useEffect(() => {
    if (mentionGuardRef) mentionGuardRef.current = mention !== null
    return () => {
      if (mentionGuardRef) mentionGuardRef.current = false
    }
  }, [mention, mentionGuardRef])

  const post = useAddComment()

  const insertMention = (login: string) => {
    if (!mention) return
    const next = body.slice(0, mention.start) + login + ' ' + body.slice(caret)
    setBody(next)
    const newCaret = mention.start + login.length + 1
    requestAnimationFrame(() => {
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(newCaret, newCaret)
      setCaret(newCaret)
    })
  }

  const submit = () => {
    const text = body.trim()
    if (!text || post.isPending) return
    post.mutate(
      {
        issue,
        body: text,
        author: { login: me.login, avatarUrl: me.avatarUrl },
        optimisticId: `optimistic-${issue.id}-${optimisticSeq.current++}`,
      },
      {
        onSuccess: () => {
          setBody('')
          setPreview(false)
        },
      },
    )
  }

  const syncCaret = () => setCaret(textareaRef.current?.selectionStart ?? 0)

  return (
    <div className="mt-2">
      <div className="mb-1 flex items-center gap-1">
        <Button
          size="sm"
          variant={preview ? 'ghost' : 'secondary'}
          className="h-5 px-2 text-[11px]"
          onClick={() => setPreview(false)}
        >
          Write
        </Button>
        <Button
          size="sm"
          variant={preview ? 'secondary' : 'ghost'}
          className="h-5 px-2 text-[11px]"
          onClick={() => setPreview(true)}
          disabled={!body.trim()}
        >
          Preview
        </Button>
      </div>
      {preview ? (
        <RichContent
          markdown={body}
          className="border-border rounded-md border px-3 py-2 text-xs"
        />
      ) : (
        <div className="relative">
          <Textarea
            ref={textareaRef}
            value={body}
            rows={3}
            placeholder="Write a comment… (@ to mention, markdown supported)"
            className="text-xs"
            onChange={(e) => {
              setBody(e.target.value)
              setCaret(e.target.selectionStart ?? 0)
              setHighlight(0)
              setMentionDismissed(false)
            }}
            onClick={syncCaret}
            onKeyUp={(e) => {
              if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) syncCaret()
            }}
            onKeyDown={(e) => {
              if (mention) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setHighlight((h) => Math.min(h + 1, mention.matches.length - 1))
                  return
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setHighlight((h) => Math.max(h - 1, 0))
                  return
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault()
                  insertMention(mention.matches[highlight]?.login ?? mention.matches[0]!.login)
                  return
                }
                if (e.key === 'Escape') {
                  // Dismiss the suggestion list without closing the whole Sheet.
                  // stopImmediatePropagation on the native event stops Radix's
                  // document-level Escape listener — React's stopPropagation
                  // alone doesn't reach it.
                  e.preventDefault()
                  e.nativeEvent.stopImmediatePropagation()
                  setMentionDismissed(true)
                  return
                }
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                submit()
              }
            }}
          />
          {mention && (
            <div
              role="listbox"
              aria-label="Mention a user"
              className="border-border bg-popover absolute right-0 left-0 z-20 mt-1 overflow-hidden rounded-md border shadow-[var(--shadow-pop)]"
            >
              {mention.matches.map((user, i) => (
                <button
                  key={user.login}
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs',
                    i === highlight ? 'bg-muted' : 'hover:bg-hover',
                  )}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => insertMention(user.login)}
                >
                  <UserAvatar
                    user={{ login: user.login, name: user.name, avatarUrl: user.avatarUrl }}
                    className="size-4"
                  />
                  <span className="font-medium">{user.login}</span>
                  {user.name && <span className="text-faint-foreground">{user.name}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-2">
        <a
          href={`${issue.url}#issuecomment-new`}
          target="_blank"
          rel="noreferrer"
          className="text-faint-foreground inline-flex items-center gap-1 text-[11px] hover:underline"
          title="File uploads and advanced formatting live on GitHub"
        >
          Comment on GitHub
          <ExternalLink className="size-3" />
        </a>
        <span className="flex-1" />
        <Button size="sm" className="h-6 text-xs" onClick={submit} disabled={!body.trim() || post.isPending}>
          Comment
          <kbd className="ml-1 font-mono text-[10px] opacity-75">⌘↵</kbd>
        </Button>
      </div>
    </div>
  )
}
